import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import UserSessionForm from "./UserSessionForm";
import { createUserSession } from "../../store/slices/userSessionSlice";

const AddUserSession = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const createDefaultSession = () => ({
        sessionCategoryMasterId: "",
        sessionMasterId: "",
        subSessionMasterId: "",
        patientId: "",
        summaryOfSession: "",
        date: new Date().toISOString().split("T")[0],
        time: new Date().toTimeString().slice(0, 5),
        satisfaction: "",
        satisfactionFeedback: "",
    });

    const [sessions, setSessions] = useState([createDefaultSession()]);
    const [expandedIndices, setExpandedIndices] = useState([0]);
    const [errors, setErrors] = useState({});

    const handleAddRow = () => {
        const nextIndex = sessions.length;
        setExpandedIndices([nextIndex]);
        setSessions((prev) => [...prev, createDefaultSession()]);
    };

    const handleRemoveRow = (index) => {
        setSessions((prev) => prev.filter((_, i) => i !== index));
        // Shift expanded indices down
        setExpandedIndices((prev) =>
            prev
                .filter((i) => i !== index)
                .map((i) => (i > index ? i - 1 : i))
        );
        // Clear and shift errors associated with this index
        setErrors((prev) => {
            const updated = {};
            Object.keys(prev).forEach((k) => {
                const ik = parseInt(k);
                if (ik < index) {
                    updated[ik] = prev[ik];
                } else if (ik > index) {
                    updated[ik - 1] = prev[ik];
                }
            });
            return updated;
        });
    };

    const toggleRow = (index) => {
        setExpandedIndices((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index)
                : [...prev, index]
        );
    };

    const handleChange = (index, e) => {
        const { name, value } = e.target;
        setSessions((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [name]: value,
            };
            return updated;
        });
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        sessions.forEach((session, index) => {
            const rowErrors = {};
            if (!session.patientId) rowErrors.patientId = "Patient is required";
            if (!session.sessionCategoryMasterId) rowErrors.sessionCategoryMasterId = "Session Category is required";
            if (!session.sessionMasterId) rowErrors.sessionMasterId = "Session Type is required";
            if (!session.subSessionMasterId) rowErrors.subSessionMasterId = "Sub Session Type is required";
            if (!session.date) rowErrors.date = "Date is required";
            if (!session.time) rowErrors.time = "Time is required";
            if (!session.satisfaction) rowErrors.satisfaction = "Satisfaction rating is required";

            if (Object.keys(rowErrors).length > 0) {
                newErrors[index] = rowErrors;
                isValid = false;
            }
        });

        setErrors(newErrors);

        // Auto-expand rows that have errors
        if (!isValid) {
            const errorIndices = Object.keys(newErrors).map(Number);
            setExpandedIndices(errorIndices);
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                title: "Validation Error",
                text: "Please correct the errors in the highlighted sessions.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            // Build bulk sessions payload
            const payload = {
                sessions: sessions.map((session) => ({
                    session_category_master_id: session.sessionCategoryMasterId,
                    session_master_id: session.sessionMasterId,
                    sub_session_master_id: session.subSessionMasterId,
                    patient_id: session.patientId,
                    summary_of_session: session.summaryOfSession,
                    date: session.date,
                    time: session.time,
                    satisfaction: session.satisfaction,
                    satisfaction_feedback: session.satisfactionFeedback,
                    status: "active",
                })),
            };

            await dispatch(createUserSession(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: `${sessions.length} Session(s) created successfully.`,
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate("/sessions");
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: typeof err === "object" ? Object.values(err).flat().join(" ") : (err.message || "Failed to create session records"),
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <form onSubmit={handleSubmit}>
                    {sessions.map((session, index) => {
                        const isExpanded = expandedIndices.includes(index);
                        return (
                            <div className="card mb-4 border border-1 border-primary-subtle" key={index}>
                                <div
                                    className="card-header d-flex justify-content-between align-items-center bg-light py-2 px-4"
                                    style={{ cursor: "pointer", userSelect: "none" }}
                                    onClick={() => toggleRow(index)}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <i className={`ri-arrow-${isExpanded ? "down" : "right"}-s-line text-secondary ri-lg`}></i>
                                        <h6 className="mb-0 text-primary fw-bold">Session Record #{index + 1}</h6>
                                        {!isExpanded && (
                                            <span className="badge bg-label-secondary ms-2 small">
                                                (Click to expand / edit)
                                            </span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        {sessions.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleRemoveRow(index)}
                                            >
                                                <i className="ri-delete-bin-line me-1"></i> Remove Row
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className="card-body p-0 border-top">
                                        {errors[index] && (
                                            <div className="alert alert-danger mx-4 mt-3 mb-0 py-2">
                                                {Object.values(errors[index]).map((err, i) => (
                                                    <div key={i} className="small">{err}</div>
                                                ))}
                                            </div>
                                        )}
                                        <UserSessionForm
                                            formData={session}
                                            handleChange={(e) => handleChange(index, e)}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className="row mt-3 mb-5">
                        <div className="col-md-3">
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100"
                                onClick={handleAddRow}
                            >
                                <i className="ri-add-circle-line me-1"></i> Add Another Row
                            </button>
                        </div>
                        <div className="col-md-2 ms-auto">
                            <button type="submit" className="btn btn-primary w-100">
                                <i className="ri-save-3-line me-1"></i> Save All
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default AddUserSession;
